export interface ParsedClient {
    companyName: string;
    vatNumber: string;
    email?: string;
    contactEmail?: string;
    responsiblePerson?: string;
    taxOffice?: string;
    businessActivity?: string;
    fullAddress?: string;
    street?: string;
    city?: string;
    postalCode?: string;
    mobilePhone?: string;
    landline?: string;
    website?: string;
    assignedTeamMember?: string;

    // Derived/Validation
    rowNumber: number;
    isValid: boolean;
    errors: string[];
}

export const isValidGreekVat = (vat: string): boolean => {
    // For CSV Import, we relax the validation to allow:
    // 1. Valid Greek VATs
    // 2. Dummy VATs (e.g., 123456789) often used in testing
    // 3. EU VATs with prefixes (e.g., EL00081566)

    if (!vat || typeof vat !== 'string') {
        return false;
    }

    const cleaned = vat.replace(/[\s-]/g, '').toUpperCase();

    // Basic alphanumeric check, 5 to 15 characters
    const re = /^[A-Z0-9]{5,15}$/;
    return re.test(cleaned);
};

export const isValidEmailFormat = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
};

export const processCsvToClients = (
    data: any[],
    mapping: Record<string, string>
): ParsedClient[] => {
    const seenVats = new Set<string>();

    return data.map((row, index) => {
        const client: ParsedClient = {
            companyName: row[mapping.companyName]?.toString().trim() || '',
            vatNumber: row[mapping.vatNumber]?.toString().trim() || '',
            email: row[mapping.email]?.toString().trim() || '',
            contactEmail: row[mapping.contactEmail]?.toString().trim() || '',
            responsiblePerson: row[mapping.responsiblePerson]?.toString().trim() || '',
            taxOffice: row[mapping.taxOffice]?.toString().trim() || '',
            businessActivity: row[mapping.businessActivity]?.toString().trim() || '',
            fullAddress: row[mapping.fullAddress]?.toString().trim() || '',
            street: row[mapping.street]?.toString().trim() || '',
            city: row[mapping.city]?.toString().trim() || '',
            postalCode: row[mapping.postalCode]?.toString().trim() || '',
            mobilePhone: row[mapping.mobilePhone]?.toString().trim() || '',
            landline: row[mapping.landline]?.toString().trim() || '',
            website: row[mapping.website]?.toString().trim() || '',
            assignedTeamMember: row[mapping.assignedTeamMember]?.toString().trim() || '',

            rowNumber: index + 2, // +1 for 0-index, +1 for header row
            isValid: true,
            errors: []
        };

        // Validate Required
        if (!client.companyName) client.errors.push('Λείπει η επωνυμία');
        if (!client.vatNumber) {
            client.errors.push('Λείπει το ΑΦΜ');
        } else {
            // Clean VAT (remove whitespace/dashes)
            client.vatNumber = client.vatNumber.replace(/[\s-]/g, '');
            if (!isValidGreekVat(client.vatNumber)) {
                client.errors.push('Μη έγκυρο ΑΦΜ');
            } else if (seenVats.has(client.vatNumber)) {
                client.errors.push('Διπλότυπο ΑΦΜ στο αρχείο CSV');
            }
            seenVats.add(client.vatNumber);
        }

        // Validate Email
        if (client.email && !isValidEmailFormat(client.email)) {
            client.errors.push('Μη έγκυρο Email Εισόδου');
        }
        if (client.contactEmail && !isValidEmailFormat(client.contactEmail)) {
            client.errors.push('Μη έγκυρο Email Επικοινωνίας');
        }

        client.isValid = client.errors.length === 0;
        return client;
    });
};
